import { Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { CircleArrowLeft } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useGetParentsBulkImportJobs } from '~entities/parent/api/get-parents-bulk-import-jobs';
import type { ParentsBulkImportJobListItem } from '~entities/parent/api/get-parents-bulk-import-jobs';
import { useGetParentsBulkImportStatus } from '~entities/parent/api/get-parents-bulk-import-status';

import { BulkImportActionRow } from '~pages/parents/bulk-upload/bulk-import-action-row';
import {
  buildActionRowContextFromUnresolved,
  buildActionRowContextFromWarning,
} from '~pages/parents/bulk-upload/import-resolution-playbook';

import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '~shared/shadcn/ui/accordion';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

type HistoryTab = 'all' | 'needs_action' | 'action_items';
const columnHelper = createColumnHelper<ParentsBulkImportJobListItem>();

export function BulkUploadImportHistoryPage() {
  const [tab, setTab] = useState<HistoryTab>('all');
  const needsAction = tab === 'needs_action' || tab === 'action_items';

  const { data, isLoading } = useGetParentsBulkImportJobs({
    page: 1,
    limit: 50,
    needsAction,
  });

  const jobs = useMemo(() => data?.jobs ?? [], [data?.jobs]);

  const jobsWithActionItems = useMemo(
    () => jobs.filter((j) => j.actionItemCount > 0),
    [jobs],
  );
  const columns = useMemo(
    () => [
      columnHelper.accessor((job) => job.completedAt ?? job.createdAt, {
        id: 'date',
        header: 'Date',
        cell: (info) => format(new Date(info.getValue()), 'MMM d, yyyy h:mm a'),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => <span className='capitalize'>{info.getValue()}</span>,
      }),
      columnHelper.accessor('totalRecords', { header: 'Parents' }),
      columnHelper.accessor('studentsCreated', {
        header: 'Students created',
      }),
      columnHelper.accessor('warningsCount', { header: 'Warnings' }),
      columnHelper.accessor('needsAttentionCount', {
        header: 'Needs attention',
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <Button variant='link' size='sm' asChild>
            <Link
              to='/parents/bulk-upload/results/$jobId'
              params={{ jobId: row.original.jobId }}
            >
              View results
            </Link>
          </Button>
        ),
      }),
    ],
    [],
  );

  const tabFilters = (
    <div className='flex flex-wrap gap-2'>
      <Button
        variant={tab === 'all' ? 'default' : 'outline'}
        size='sm'
        onClick={() => {
          setTab('all');
        }}
      >
        All imports
      </Button>
      <Button
        variant={tab === 'needs_action' ? 'default' : 'outline'}
        size='sm'
        onClick={() => {
          setTab('needs_action');
        }}
      >
        Needs action
      </Button>
      <Button
        variant={tab === 'action_items' ? 'default' : 'outline'}
        size='sm'
        onClick={() => {
          setTab('action_items');
        }}
      >
        All action items
      </Button>
    </div>
  );

  return (
    <div className='flex min-h-screen flex-col p-4'>
      <Link
        className='mb-6 flex w-fit items-center gap-2'
        to='/parents/bulk-upload'
      >
        <CircleArrowLeft className='size-6' />
        <span className='text-lg font-semibold'>Back to bulk upload</span>
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>Import history</CardTitle>
        </CardHeader>
        <CardContent>
          {tab === 'action_items' ? (
            <div className='space-y-4'>
              {tabFilters}
              {isLoading ? (
                <p className='text-sm text-muted-foreground'>Loading…</p>
              ) : (
                <ActionItemsFlatList jobs={jobsWithActionItems} />
              )}
            </div>
          ) : (
            <EnrollioTable
              totalLabel='Imports'
              filterHeader={(table) => (
                <>
                  <EnrollioTableSearch
                    table={table}
                    placeholder='Search imports...'
                  />
                  {tabFilters}
                </>
              )}
              data={jobs}
              columns={columns}
              getRowId={(row) => row.jobId}
              isUpdating={isLoading}
              emptyContent={
                needsAction
                  ? 'No imports need follow-up right now.'
                  : 'No import history yet.'
              }
              ariaLabel='Parent import history'
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ActionItemsFlatList({
  jobs,
}: {
  jobs: Array<ParentsBulkImportJobListItem>;
}) {
  const [openJobIds, setOpenJobIds] = useState<Array<string>>([]);

  if (jobs.length === 0) {
    return (
      <p className='text-sm text-muted-foreground'>
        No action items across recent imports.
      </p>
    );
  }

  return (
    <Accordion
      type='multiple'
      value={openJobIds}
      onValueChange={setOpenJobIds}
      className='space-y-3'
    >
      {jobs.map((job) => (
        <AccordionItem
          key={job.jobId}
          value={job.jobId}
          className='rounded-md border px-3'
        >
          <AccordionTrigger className='py-3 hover:no-underline'>
            <div className='flex flex-1 items-center justify-between gap-4 pr-2'>
              <span className='text-sm font-medium'>
                Import{' '}
                {job.completedAt
                  ? format(new Date(job.completedAt), 'MMM d, yyyy')
                  : format(new Date(job.createdAt), 'MMM d, yyyy')}
              </span>
              <span className='text-xs text-muted-foreground'>
                {job.actionItemCount} action item
                {job.actionItemCount !== 1 ? 's' : ''}
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            {openJobIds.includes(job.jobId) ? (
              <ActionItemsForJob jobId={job.jobId} />
            ) : null}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

function ActionItemsForJob({ jobId }: { jobId: string }) {
  const { data, isLoading } = useGetParentsBulkImportStatus({ jobId });
  const result = data?.job.result;

  if (isLoading) {
    return (
      <p className='text-sm text-muted-foreground'>Loading action items…</p>
    );
  }
  if (!result) return null;

  const rows = [
    // The API omits these arrays on older completed jobs.
    ...(result.importWarnings ?? []).map((w) =>
      buildActionRowContextFromWarning({
        ...w,
        manualResolutionKey: w.manualResolutionKey,
      }),
    ),
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    ...(result.duplicateUnresolved ?? []).map((d) =>
      buildActionRowContextFromUnresolved({
        ...d,
        manualResolutionKey: d.manualResolutionKey,
      }),
    ),
  ];

  if (rows.length === 0) return null;

  return (
    <div className='space-y-3'>
      <div className='flex justify-end'>
        <Button variant='link' size='sm' asChild>
          <Link to='/parents/bulk-upload/results/$jobId' params={{ jobId }}>
            View full results
          </Link>
        </Button>
      </div>
      <div className='space-y-3'>
        {rows.map((row, idx) => (
          <BulkImportActionRow key={`${jobId}-${idx}`} row={row} />
        ))}
      </div>
    </div>
  );
}
